import * as React from 'react';
import { BodyCell } from '@tableau/widgets-datagrid';
import { tablesWithCrassCellFormatters } from '@tableau/catalog-ui-temp';
import { CommonColumns, IColumnFactoryParameters } from '../ListViewGridColumnGenerator';
import * as Layout from '../../../../common/styles/Layout';
import { IconStyle, LabelIconStyle } from '../../../utils/DataCatalogUtils';
import { LabelWrapper } from './Labels';
import { SessionManagementService } from '../../../services/SessionManagementService';
import { Messages } from '../../../../../gen-src/Localize';
import { defaultHeaderFormatter } from '../DefaultHeaderFormatter';
import { LocalizedCountCell, PlainTextCell, PlainTextCellWithTransform } from './PlainTextAndNumberCells';

export function TableNameCell(parameters: IColumnFactoryParameters): React.Factory<BodyCell.Props> {
  return React.createFactory(function TableNameCell(props: BodyCell.Props) {
    const nameFactory = tablesWithCrassCellFormatters.name.cellFormatter;
    const typeFactory = tablesWithCrassCellFormatters.typeForCrass.cellFormatter;

    const labels = LabelWrapper(
      {
        catalogEnabled: SessionManagementService.instance.catalogingOnSiteEnabled(),
        content: {
          contentId: props.row.data.graphQLId,
          contentLuid: props.row.data.luid,
          contentType: 'table',
          contentName: props.row.data.name,
          subjectArea: props.row.data.__typename,
        },
        includeUpstream: false,
        includeParented: false,
        probablyOrEager: {
          certified: props.row.data.isCertified,
          warning: props.row.data.crassData?.hasActiveDataQualityWarning
            ? { count: 1, value: 'Warning', severe: props.row.data.crassData.hasSevereDataQualityWarning }
            : undefined,
        },
        testId: props.row.data.__typename + '-search-label',
        controls: {
          kind: 'icon',
          siteUrls: parameters.listViewParams.urls,
        },
      },
      parameters.listViewParams.urls,
      props.row.data.crassData?.siteLuid
    );

    // the nameFactory in data-catalog-client-components need to use id with GraphQL id to generate the link.
    // so here we have to assign id ot the GraphQL id.
    props.row.data.id = props.row.data.graphQLId;

    return (
      <div className={Layout.flexH} style={{ width: '100%' }}>
        <div className={IconStyle}>{typeFactory(props)}</div>
        <div className={LabelIconStyle}>{labels}</div>
        <div className={Layout.flexAuto} style={{ overflow: 'hidden' }}>
          {nameFactory(props)}
        </div>
      </div>
    );
  });
}

export const TableNameColumn = (parameters: IColumnFactoryParameters) => {
  return {
    name: 'tableName',
    dataKey: 'tableName',
    sortable: false,
    headerText: Messages.column_name(),
    growToFit: true,
    headerFormatter: defaultHeaderFormatter(),
    cellFormatter: TableNameCell(parameters),
  };
};
export const TableTypeColumn = (parameters: IColumnFactoryParameters) => {
  return {
    name: 'tableType',
    dataKey: 'tableType',
    sortable: false,
    headerText: Messages.column_content_type(),
    growToFit: true,
    headerFormatter: defaultHeaderFormatter(),
    cellFormatter: PlainTextCellWithTransform('tableType', tableTypeToLocalizedStrings),
  };
};
export const TableWorkbooksCountColumn = (parameters: IColumnFactoryParameters) => {
  return {
    name: 'tableinworkbookscount',
    dataKey: 'tableinworkbookscount',
    sortable: false,
    direction: 'desc',
    headerText: Messages.column_numWorkbooks(),
    headerFormatter: defaultHeaderFormatter(),
    cellFormatter: LocalizedCountCell('workbookCount'),
  };
};
export const TableDataSourcesCountColumn = (parameters: IColumnFactoryParameters) => {
  return {
    name: 'tableindatasourcecount',
    dataKey: 'tableindatasourcecount',
    sortable: false,
    direction: 'desc',
    headerText: Messages.column_numDatasources(),
    headerFormatter: defaultHeaderFormatter(),
    cellFormatter: LocalizedCountCell('datasourceCount'),
  };
};
export const TableDatabaseNameColumn = (parameters: IColumnFactoryParameters) => {
  return {
    name: 'database',
    dataKey: 'database',
    sortable: false,
    direction: 'desc',
    growToFit: true,
    headerText: Messages.column_table_database(),
    headerFormatter: defaultHeaderFormatter(),
    cellFormatter: tablesWithCrassCellFormatters.database.cellFormatter,
  };
};
export const TableSourceColumn = (parameters: IColumnFactoryParameters) => {
  return {
    name: 'database',
    dataKey: 'database',
    sortable: false,
    direction: 'desc',
    growToFit: true,
    headerText: Messages.column_source(),
    headerFormatter: defaultHeaderFormatter(),
    cellFormatter: PlainTextCell('source'),
  };
};

export const TableColumnsWithType = [
  TableNameColumn,
  TableTypeColumn,
  TableWorkbooksCountColumn,
  TableDataSourcesCountColumn,
  TableSourceColumn,
  CommonColumns.description,
  CommonColumns.location,
];

export const TableColumns = [
  TableNameColumn,
  TableWorkbooksCountColumn,
  TableDataSourcesCountColumn,
  TableDatabaseNameColumn,
  CommonColumns.description,
  CommonColumns.location,
];

function tableTypeToLocalizedStrings(tableType: string): string {
  switch (tableType) {
    case 'DataModel':
      return Messages.DataModelObject();
    case 'DataLake':
      return Messages.DataLakeObject();
    case 'CalculatedInsight':
      return Messages.CalculatedInsight();
    case 'DatabaseTable':
    default:
      return Messages.Table();
  }
}
